import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, Save, UserCircle, Loader2, Trash2, FileText,
  Eye, Upload, RefreshCw, FileBadge, AlertTriangle, CheckCircle2,
  Download, Mail, Plus,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  full_name: "",
  payroll_email: "",
  personal_email: "",
  job_title: "",
  employment_type: "part_time",
  country: "",
  province: "",
  city: "",
  start_date: "",
  end_date: "",
  gross_salary: "",
  currency: "PKR",
  bank_name: "",
  payment_method: "",
  status: "active",
};

const CONTRACT_STATUS_META = {
  draft:      { label: "Draft",      cls: "bg-slate-100 text-slate-600", Icon: FileText },
  sent:       { label: "Sent",       cls: "bg-blue-100 text-blue-700",   Icon: FileText },
  signed:     { label: "Signed",     cls: "bg-green-100 text-green-700", Icon: CheckCircle2 },
  superseded: { label: "Superseded", cls: "bg-slate-100 text-slate-400", Icon: AlertTriangle },
};

function StatusBadge({ value, meta }) {
  const m = meta[value] || { label: value, cls: "bg-slate-100 text-slate-600" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${m.cls}`}>
      {m.label}
    </span>
  );
}

function Field({ label, children, hint }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-slate-700">{label}</Label>
      {children}
      {hint && <p className="text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

// ── Upload Signed Dialog ───────────────────────────────────────────────────

function UploadSignedDialog({ contract, onClose, onDone, getAuthHeaders }) {
  const [signingDate, setSigningDate] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== "application/pdf") {
      toast.error("Only PDF files are accepted for signed contracts");
      return;
    }
    setFile(f);
  };

  const handleSubmit = async () => {
    if (!signingDate.trim()) { toast.error("Signing date is required"); return; }
    if (!file) { toast.error("Please select the signed PDF"); return; }
    setSaving(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result.split(",")[1]);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });
      await axios.post(
        `${API}/employees/${contract.employee_id}/contracts/${contract.id}/upload-signed`,
        { file_data: b64, file_name: file.name, mime_type: file.type, signing_date: signingDate },
        { headers: getAuthHeaders() },
      );
      toast.success("Signed contract uploaded.");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md space-y-4"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="font-semibold text-slate-800 flex items-center gap-2">
          <Upload className="w-4 h-4" /> Upload Signed Contract — Version {contract.version}
        </h3>
        <p className="text-xs text-slate-500">
          Upload the signed copy of the generated PDF. The signing date must be the actual date
          the agreement was signed — it will not be set automatically.
        </p>

        <Field label="Signing Date *" hint="Actual date the employee signed the agreement">
          <Input type="date" value={signingDate} onChange={e => setSigningDate(e.target.value)} />
        </Field>

        <Field label="Signed PDF *">
          <div className="flex items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={handleFile}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileRef.current?.click()}
              className="gap-2"
            >
              <Upload className="w-4 h-4" />
              {file ? file.name : "Choose PDF…"}
            </Button>
            {file && <span className="text-xs text-green-700">{(file.size / 1024).toFixed(0)} KB</span>}
          </div>
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {saving ? "Uploading…" : "Upload Signed"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Supersede Dialog ───────────────────────────────────────────────────────

function SupersedeDialog({ contract, allContracts, onClose, onDone, getAuthHeaders }) {
  const signedOthers = allContracts.filter(c => c.status === "signed" && c.id !== contract.id);
  const [replacementId, setReplacementId] = useState(signedOthers[0]?.id || "");
  const [saving, setSaving] = useState(false);

  if (signedOthers.length === 0) {
    return (
      <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
          <h3 className="font-semibold text-slate-800 mb-3">Supersede Contract</h3>
          <p className="text-sm text-slate-600 mb-4">
            There are no other signed contracts available as a replacement.
            Upload and mark a replacement contract as signed first.
          </p>
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async () => {
    if (!replacementId) { toast.error("Select a replacement contract"); return; }
    setSaving(true);
    try {
      await axios.post(
        `${API}/employees/${contract.employee_id}/contracts/${contract.id}/supersede`,
        { replacement_contract_id: replacementId },
        { headers: getAuthHeaders() },
      );
      toast.success("Contract superseded.");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Supersession failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md space-y-4"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="font-semibold text-slate-800 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-500" />
          Supersede Contract — Version {contract.version}
        </h3>
        <p className="text-xs text-slate-500">
          This marks Version {contract.version} as superseded. Select the replacement signed
          contract. This action cannot be undone. Both contracts will be preserved.
        </p>

        <Field label="Replacement Contract (must be signed)">
          <Select value={replacementId} onValueChange={setReplacementId}>
            <SelectTrigger>
              <SelectValue placeholder="Select replacement…" />
            </SelectTrigger>
            <SelectContent>
              {signedOthers.map(c => (
                <SelectItem key={c.id} value={c.id}>
                  Version {c.version} — signed {c.signing_date || "(date not recorded)"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={saving}
            className="bg-amber-600 hover:bg-amber-700 text-white gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
            {saving ? "Superseding…" : "Confirm Supersede"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Contracts Tab ──────────────────────────────────────────────────────────

function ContractsTab({ employeeId, getAuthHeaders }) {
  const [contracts, setContracts] = useState([]);
  const [loadingContracts, setLoadingContracts] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [uploadTarget, setUploadTarget] = useState(null);   // contract being signed
  const [supersedeTarget, setSupersedeTarget] = useState(null); // contract being superseded

  const loadContracts = useCallback(async () => {
    setLoadingContracts(true);
    try {
      const res = await axios.get(`${API}/employees/${employeeId}/contracts`, { headers: getAuthHeaders() });
      setContracts(res.data || []);
    } catch {
      toast.error("Failed to load contracts");
    } finally {
      setLoadingContracts(false);
    }
  }, [employeeId, getAuthHeaders]);

  useEffect(() => { loadContracts(); }, [loadContracts]);

  const handlePreview = async () => {
    try {
      const res = await axios.post(
        `${API}/employees/${employeeId}/contracts/preview`,
        {},
        { headers: getAuthHeaders() },
      );
      const blob = new Blob([res.data.html], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    }
  };

  const handleGenerate = async () => {
    if (!window.confirm(
      "Generate a new contract PDF from current employee data? This creates a new version record."
    )) return;
    setGenerating(true);
    try {
      await axios.post(
        `${API}/employees/${employeeId}/contracts/generate`,
        {},
        { headers: getAuthHeaders() },
      );
      toast.success("Contract generated.");
      loadContracts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="p-5 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">
          Employment Contracts
        </h2>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadContracts}
            disabled={loadingContracts}
            className="gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loadingContracts ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handlePreview}
            className="gap-2"
          >
            <Eye className="w-4 h-4" />
            Preview
          </Button>
          <Button
            size="sm"
            onClick={handleGenerate}
            disabled={generating}
            className="gap-2 bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileBadge className="w-4 h-4" />}
            {generating ? "Generating…" : "Generate New Contract"}
          </Button>
        </div>
      </div>

      <p className="text-xs text-slate-400">
        Preview reflects current employee data. Generate creates a new PDF version stored in R2.
        Upload a signed copy only after the physical agreement has been signed — enter the actual signing date.
      </p>

      {/* Contract list */}
      {loadingContracts ? (
        <div className="flex items-center justify-center h-24 text-slate-400 text-sm gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : contracts.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-24 text-slate-400 gap-2">
          <FileText className="w-8 h-8" />
          <p className="text-sm">No contracts yet. Preview and then generate the first version.</p>
        </div>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="text-left px-4 py-2.5 font-semibold text-slate-600">Version</th>
                <th className="text-left px-4 py-2.5 font-semibold text-slate-600">Status</th>
                <th className="text-left px-4 py-2.5 font-semibold text-slate-600">Generated</th>
                <th className="text-left px-4 py-2.5 font-semibold text-slate-600">Signing Date</th>
                <th className="text-left px-4 py-2.5 font-semibold text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {[...contracts].reverse().map(c => (
                <tr key={c.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
                  <td className="px-4 py-2.5 font-medium text-slate-800">v{c.version}</td>
                  <td className="px-4 py-2.5">
                    <StatusBadge value={c.status} meta={CONTRACT_STATUS_META} />
                  </td>
                  <td className="px-4 py-2.5 text-slate-500 text-xs">
                    {c.document_created_at
                      ? new Date(c.document_created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                      : "—"}
                  </td>
                  <td className="px-4 py-2.5 text-slate-500 text-xs">
                    {c.signing_date || "—"}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* View generated */}
                      {c.document_url && (
                        <a
                          href={c.document_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline"
                        >
                          <Download className="w-3.5 h-3.5" /> Generated
                        </a>
                      )}
                      {/* View signed */}
                      {c.signed_document_url && (
                        <a
                          href={c.signed_document_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-green-700 hover:underline"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" /> Signed
                        </a>
                      )}
                      {/* Upload signed — only if not yet signed and not superseded */}
                      {(c.status === "draft" || c.status === "sent") && (
                        <button
                          onClick={() => setUploadTarget(c)}
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                        >
                          <Upload className="w-3.5 h-3.5" /> Upload Signed
                        </button>
                      )}
                      {/* Supersede — only if signed */}
                      {c.status === "signed" && (
                        <button
                          onClick={() => setSupersedeTarget(c)}
                          className="inline-flex items-center gap-1 text-xs text-amber-600 hover:underline"
                        >
                          <AlertTriangle className="w-3.5 h-3.5" /> Supersede
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Dialogs */}
      {uploadTarget && (
        <UploadSignedDialog
          contract={uploadTarget}
          onClose={() => setUploadTarget(null)}
          onDone={() => { setUploadTarget(null); loadContracts(); }}
          getAuthHeaders={getAuthHeaders}
        />
      )}
      {supersedeTarget && (
        <SupersedeDialog
          contract={supersedeTarget}
          allContracts={contracts}
          onClose={() => setSupersedeTarget(null)}
          onDone={() => { setSupersedeTarget(null); loadContracts(); }}
          getAuthHeaders={getAuthHeaders}
        />
      )}
    </div>
  );
}

// ── Payslips Tab ───────────────────────────────────────────────────────────

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

function PayslipsTab({ employeeId, getAuthHeaders }) {
  const [payslips, setPayslips]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [generating, setGenerating] = useState(false);
  const [emailing, setEmailing]     = useState(null);
  const [showForm, setShowForm]     = useState(false);

  const now   = new Date();
  const [year, setYear]   = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth() + 1));

  const loadPayslips = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/employees/${employeeId}/payslips`, { headers: getAuthHeaders() });
      setPayslips(res.data || []);
    } catch {
      toast.error("Failed to load payslips");
    } finally {
      setLoading(false);
    }
  }, [employeeId, getAuthHeaders]);

  useEffect(() => { loadPayslips(); }, [loadPayslips]);

  const handleGenerate = async () => {
    const y = parseInt(year, 10);
    const m = parseInt(month, 10);
    if (!y || m < 1 || m > 12) { toast.error("Enter a valid year and month"); return; }
    setGenerating(true);
    try {
      await axios.post(
        `${API}/employees/${employeeId}/payslips`,
        { period_year: y, period_month: m },
        { headers: getAuthHeaders() },
      );
      toast.success(`Payslip for ${MONTHS[m-1]} ${y} generated.`);
      setShowForm(false);
      loadPayslips();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (slip) => {
    const url      = `${API}/employees/${employeeId}/payslips/${slip.id}/pdf`;
    const filename = `Payslip-${slip.employee_snapshot?.full_name?.replace(/\s+/g, "-") || "Employee"}-${MONTHS[slip.period_month - 1]}-${slip.period_year}.pdf`;
    try {
      const res = await axios.get(url, {
        headers: getAuthHeaders(),
        responseType: "blob",
      });
      const objectUrl = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a         = document.createElement("a");
      a.href          = objectUrl;
      a.setAttribute("download", filename);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) {
        toast.error("Access denied — admin role required to download payslips");
      } else if (status === 404) {
        toast.error("Payslip not found");
      } else {
        toast.error(e?.response?.data?.detail || "PDF download failed");
      }
    }
  };

  const handleEmail = async (slip) => {
    setEmailing(slip.id);
    try {
      const res = await axios.post(
        `${API}/employees/${employeeId}/payslips/${slip.id}/email`,
        {},
        { headers: getAuthHeaders() },
      );
      toast.success(`Payslip sent to ${res.data.sent_to}`);
      loadPayslips();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Email failed");
    } finally {
      setEmailing(null);
    }
  };

  return (
    <div className="p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Payslips</h2>
        <Button size="sm" variant="outline" onClick={() => setShowForm(v => !v)} className="gap-2">
          <Plus className="w-4 h-4" />
          Generate Payslip
        </Button>
      </div>

      {showForm && (
        <div className="border border-slate-200 rounded-lg p-4 bg-slate-50 space-y-3">
          <p className="text-xs text-slate-500">Select the pay period to generate a payslip from the employee's current gross salary.</p>
          <div className="flex gap-3 items-end flex-wrap">
            <div className="space-y-1">
              <Label className="text-xs">Year</Label>
              <Input className="w-24" value={year} onChange={e => setYear(e.target.value)} placeholder="2026" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Month</Label>
              <Select value={month} onValueChange={setMonth}>
                <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTHS.map((m, i) => (
                    <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button size="sm" onClick={handleGenerate} disabled={generating} className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2">
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              {generating ? "Generating…" : "Generate"}
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : payslips.length === 0 ? (
        <p className="text-sm text-slate-400 py-4 text-center">No payslips yet. Generate the first one above.</p>
      ) : (
        <div className="divide-y divide-slate-100">
          {payslips.map(slip => (
            <div key={slip.id} className="flex items-center justify-between py-3 gap-3">
              <div>
                <span className="font-medium text-slate-800 text-sm">
                  {MONTHS[slip.period_month - 1]} {slip.period_year}
                </span>
                <span className="ml-3 text-xs text-slate-500">
                  {slip.currency} {slip.net_pay?.toLocaleString(undefined, { minimumFractionDigits: 2 })} net
                </span>
                {slip.email_sent_at && (
                  <span className="ml-2 text-xs text-green-600">
                    ✉ sent {slip.email_sent_at.slice(0, 10)}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDownload(slip)}
                  className="gap-1.5 text-xs"
                >
                  <Download className="w-3.5 h-3.5" />
                  PDF
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEmail(slip)}
                  disabled={emailing === slip.id}
                  className="gap-1.5 text-xs"
                >
                  {emailing === slip.id
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <Mail className="w-3.5 h-3.5" />}
                  Email
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────

export default function EmployeeDetail() {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const { agent, getAuthHeaders } = useAuth();

  const [activeTab, setActiveTab] = useState("profile");
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/employees/${id}`, { headers: getAuthHeaders() });
      const emp = res.data;
      setForm({
        full_name:       emp.full_name || "",
        payroll_email:   emp.payroll_email || "",
        personal_email:  emp.personal_email || "",
        job_title:       emp.job_title || "",
        employment_type: emp.employment_type || "part_time",
        country:         emp.country || "",
        province:        emp.province || "",
        city:            emp.city || "",
        start_date:      emp.start_date || "",
        end_date:        emp.end_date || "",
        gross_salary:    emp.gross_salary != null ? String(emp.gross_salary) : "",
        currency:        emp.currency || "PKR",
        bank_name:       emp.bank_name || "",
        payment_method:  emp.payment_method || "",
        status:          emp.status || "active",
      });
    } catch {
      toast.error("Failed to load employee");
      navigate("/employees");
    } finally {
      setLoading(false);
    }
  }, [id, isNew, getAuthHeaders, navigate]);

  useEffect(() => { load(); }, [load]);

  if (agent?.role !== "admin") {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64 text-slate-500 text-sm">
          Access restricted to administrators.
        </div>
      </DashboardLayout>
    );
  }

  const set = (field) => (e) =>
    setForm(f => ({ ...f, [field]: e.target ? e.target.value : e }));

  const validate = () => {
    if (!form.full_name.trim()) return "Full name is required.";
    if (!form.payroll_email.trim()) return "Payroll email is required.";
    if (!form.job_title.trim()) return "Job title is required.";
    if (!form.employment_type) return "Employment type is required.";
    if (form.gross_salary !== "" && isNaN(Number(form.gross_salary)))
      return "Gross salary must be a number.";
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        gross_salary: form.gross_salary !== "" ? Number(form.gross_salary) : null,
        personal_email: form.personal_email.trim() || null,
        country:        form.country.trim()  || null,
        province:       form.province.trim() || null,
        city:           form.city.trim()     || null,
        start_date:     form.start_date      || null,
        end_date:       form.end_date        || null,
        bank_name:      form.bank_name.trim()       || null,
        payment_method: form.payment_method.trim()  || null,
      };
      if (isNew) {
        const res = await axios.post(`${API}/employees`, payload, { headers: getAuthHeaders() });
        toast.success("Employee created.");
        navigate(`/employees/${res.data.id}`);
      } else {
        await axios.put(`${API}/employees/${id}`, payload, { headers: getAuthHeaders() });
        toast.success("Employee updated.");
        load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save employee");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!window.confirm("Set this employee to inactive? This can be reversed by editing their status.")) return;
    setDeactivating(true);
    try {
      await axios.delete(`${API}/employees/${id}`, { headers: getAuthHeaders() });
      toast.success("Employee set to inactive.");
      navigate("/employees");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to deactivate employee");
    } finally {
      setDeactivating(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64 text-slate-400 text-sm gap-2">
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading…
        </div>
      </DashboardLayout>
    );
  }

  const tabs = [
    { key: "profile", label: "Profile" },
    ...(!isNew ? [{ key: "contracts", label: "Contracts" }] : []),
    ...(!isNew ? [{ key: "payslips", label: "Payslips" }] : []),
  ];

  return (
    <DashboardLayout>
      <div className="p-6 max-w-3xl mx-auto">
        {/* Back link */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate("/employees")}
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-indigo-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Employees
          </button>
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
              <UserCircle className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">
                {isNew ? "New Employee" : form.full_name || "Employee"}
              </h1>
              {!isNew && (
                <p className="text-xs text-slate-400 mt-0.5">{form.job_title}</p>
              )}
            </div>
          </div>
          {activeTab === "profile" && (
            <div className="flex items-center gap-2">
              {!isNew && form.status === "active" && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDeactivate}
                  disabled={deactivating}
                  className="gap-2 text-red-600 border-red-200 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                  {deactivating ? "Deactivating…" : "Set Inactive"}
                </Button>
              )}
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="gap-2 bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          )}
        </div>

        {/* Tab bar */}
        {tabs.length > 1 && (
          <div className="flex border-b border-slate-200 mb-4 gap-1 items-center">
            {tabs.map(t => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === t.key
                    ? "border-indigo-600 text-indigo-700"
                    : "border-transparent text-slate-500 hover:text-slate-800"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm divide-y divide-slate-100">

          {activeTab === "profile" && (
            <>
              {/* Personal Information */}
              <div className="p-5 space-y-4">
                <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">
                  Personal Information
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Full Name *">
                    <Input value={form.full_name} onChange={set("full_name")} placeholder="Full legal name" />
                  </Field>
                  <Field label="Job Title *">
                    <Input value={form.job_title} onChange={set("job_title")} placeholder="e.g. Customer Support Assistant" />
                  </Field>
                  <Field label="Employment Type *">
                    <Select value={form.employment_type} onValueChange={set("employment_type")}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="full_time">Full-time</SelectItem>
                        <SelectItem value="part_time">Part-time</SelectItem>
                        <SelectItem value="contractor">Contractor</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Status">
                    <Select value={form.status} onValueChange={set("status")}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
              </div>

              {/* Contact */}
              <div className="p-5 space-y-4">
                <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Contact</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Payroll Email *" hint="Default salary-slip recipient">
                    <Input type="email" value={form.payroll_email} onChange={set("payroll_email")} placeholder="payroll@example.com" />
                  </Field>
                  <Field label="Personal Email" hint="Not used for payroll communications">
                    <Input type="email" value={form.personal_email} onChange={set("personal_email")} placeholder="personal@example.com" />
                  </Field>
                </div>
              </div>

              {/* Location */}
              <div className="p-5 space-y-4">
                <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Location</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Field label="City">
                    <Input value={form.city} onChange={set("city")} placeholder="e.g. Multan" />
                  </Field>
                  <Field label="Province">
                    <Input value={form.province} onChange={set("province")} placeholder="e.g. Punjab" />
                  </Field>
                  <Field label="Country">
                    <Input value={form.country} onChange={set("country")} placeholder="e.g. Pakistan" />
                  </Field>
                </div>
              </div>

              {/* Employment Dates */}
              <div className="p-5 space-y-4">
                <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Employment Dates</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Start Date">
                    <Input type="date" value={form.start_date} onChange={set("start_date")} />
                  </Field>
                  <Field label="End Date" hint="Leave blank if still employed">
                    <Input type="date" value={form.end_date} onChange={set("end_date")} />
                  </Field>
                </div>
              </div>

              {/* Salary */}
              <div className="p-5 space-y-4">
                <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Salary</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Gross Monthly Salary">
                    <Input type="number" min="0" step="0.01" value={form.gross_salary} onChange={set("gross_salary")} placeholder="e.g. 100000" />
                  </Field>
                  <Field label="Currency">
                    <Input value={form.currency} onChange={set("currency")} placeholder="PKR" />
                  </Field>
                </div>
              </div>

              {/* Payment */}
              <div className="p-5 space-y-4">
                <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Payment</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Bank Name">
                    <Input value={form.bank_name} onChange={set("bank_name")} placeholder="e.g. HBL" />
                  </Field>
                  <Field label="Payment Method">
                    <Input value={form.payment_method} onChange={set("payment_method")} placeholder="e.g. Bank Transfer" />
                  </Field>
                </div>
              </div>

              <div className="px-5 py-3">
                <p className="text-xs text-slate-400">
                  All fields marked * are required. Leave other fields blank and complete them later.
                </p>
              </div>
            </>
          )}

          {activeTab === "contracts" && !isNew && (
            <ContractsTab employeeId={id} getAuthHeaders={getAuthHeaders} />
          )}

          {activeTab === "payslips" && !isNew && (
            <PayslipsTab employeeId={id} getAuthHeaders={getAuthHeaders} />
          )}

        </div>
      </div>
    </DashboardLayout>
  );
}
